import { Link, router, useForm } from "@inertiajs/react"
import { Check, Info } from "lucide-react"
import { useState } from "react"

import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { billingPortalPath, checkoutPath, signUpPath } from "@/routes"

interface Plan {
  id: number
  slug: string
  name: string
  description: string
  monthly_price_cents: number
  yearly_price_cents: number
  features: string[]
}

interface Props {
  plans: Plan[]
  current_user: { id: number; email: string; plan: string } | null
  current_plan: "free" | "plus" | "pro" | null
}

const featureTooltips: Record<string, string> = {
  "5 operations per day":
    "Each merge, split, reorder, protect, sign, or image conversion counts as one operation",
  "50 operations per day":
    "Each merge, split, reorder, protect, sign, or image conversion counts as one operation",
  "100 operations per day":
    "Each merge, split, reorder, protect, sign, or image conversion counts as one operation",
  "24-hour file retention":
    "Dashboard files are automatically deleted after 24 hours",
  "Permanent file storage":
    "Files remain in your dashboard until you delete them",
  "Up to 50MB per file": "Maximum file size for each uploaded PDF",
}

const formatPrice = (cents: number) => `$${(cents / 100).toFixed(0)}`

function CheckoutButton({
  plan,
  interval,
  highlighted,
}: {
  plan: string
  interval: "month" | "year"
  highlighted: boolean
}) {
  const [processing, setProcessing] = useState(false)

  const handleClick = () => {
    setProcessing(true)
    router.post(
      checkoutPath(),
      { plan, interval },
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Button
      className="w-full"
      variant={highlighted ? "default" : "outline"}
      onClick={handleClick}
      disabled={processing}
    >
      {processing
        ? "Processing..."
        : `Upgrade to ${plan.charAt(0).toUpperCase() + plan.slice(1)}`}
    </Button>
  )
}

function ManageButton({ highlighted }: { highlighted: boolean }) {
  const form = useForm({})

  const handleClick = () => {
    form.post(billingPortalPath())
  }

  return (
    <Button
      className="w-full"
      variant={highlighted ? "default" : "outline"}
      onClick={handleClick}
      disabled={form.processing}
    >
      {form.processing ? "Processing..." : "Manage Subscription"}
    </Button>
  )
}

export default function Pricing({ plans, current_user, current_plan }: Props) {
  const [isYearly, setIsYearly] = useState(false)
  const interval = isYearly ? "year" : "month"

  const isPaidUser = current_plan === "plus" || current_plan === "pro"

  const renderButton = (plan: Plan, highlighted: boolean) => {
    const isFree = plan.slug === "free"
    const isCurrentPlan = current_plan === plan.slug

    if (isFree) {
      if (!current_user) {
        return (
          <Link href="/merge-pdf" className="block">
            <Button
              className="w-full"
              variant={highlighted ? "default" : "outline"}
            >
              Get Started
            </Button>
          </Link>
        )
      }
      if (isCurrentPlan) {
        return (
          <Button className="w-full" variant="outline" disabled>
            Current Plan
          </Button>
        )
      }
      return <ManageButton highlighted={highlighted} />
    }

    if (!current_user) {
      return (
        <Link
          href={`${signUpPath()}?plan=${plan.slug}&interval=${interval}`}
          className="block"
        >
          <Button
            className="w-full"
            variant={highlighted ? "default" : "outline"}
          >
            Upgrade to {plan.name}
          </Button>
        </Link>
      )
    }

    if (isCurrentPlan) {
      return <ManageButton highlighted={highlighted} />
    }

    if (isPaidUser) {
      return <ManageButton highlighted={highlighted} />
    }

    return (
      <CheckoutButton
        plan={plan.slug}
        interval={interval}
        highlighted={highlighted}
      />
    )
  }

  return (
    <>
      <SEO
        title="PDF Tools Pricing | Free & Pro Plans | StitchPDF"
        description="Simple, transparent pricing for StitchPDF. Start free with 5 operations per day. Upgrade for more operations and longer file retention."
        canonical="https://stitchpdf.com/pricing"
        ogTitle="PDF Tools Pricing | StitchPDF"
        ogDescription="Simple, transparent pricing. Start free, upgrade when you need more."
      />

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-16">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold">Simple, transparent pricing</h1>
            <p className="text-muted-foreground mt-4 text-lg">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="mx-auto mt-8 flex items-center justify-center gap-3">
            <Label
              htmlFor="billing-toggle"
              className={cn(
                "text-sm font-medium",
                !isYearly && "text-foreground",
                isYearly && "text-muted-foreground",
              )}
            >
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <Label
              htmlFor="billing-toggle"
              className={cn(
                "text-sm font-medium",
                isYearly && "text-foreground",
                !isYearly && "text-muted-foreground",
              )}
            >
              Yearly
              <span className="ml-1.5 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                Save 10%
              </span>
            </Label>
          </div>

          <div className="mx-auto mt-16 grid max-w-5xl gap-8 md:grid-cols-3">
            {plans.map((plan) => {
              const highlighted = plan.slug === "plus"
              const isFree = plan.slug === "free"
              const price = isFree
                ? 0
                : isYearly
                  ? plan.yearly_price_cents
                  : plan.monthly_price_cents
              const isCurrentPlan = current_plan === plan.slug

              return (
                <Card
                  key={plan.id}
                  className={cn(
                    "relative",
                    highlighted && "border-primary shadow-md",
                  )}
                >
                  {highlighted && !isCurrentPlan && (
                    <div className="bg-primary text-primary-foreground absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-medium">
                      Most Popular
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-3 right-4 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                      Current Plan
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">
                        {isFree ? "$0" : formatPrice(price)}
                      </span>
                      <span className="text-muted-foreground">
                        {isFree ? " forever" : isYearly ? "/year" : "/month"}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature) => {
                        const tooltip = featureTooltips[feature]
                        return (
                          <li
                            key={feature}
                            className="flex items-center gap-2 text-sm"
                          >
                            <Check
                              className="text-primary h-4 w-4 shrink-0"
                              aria-hidden="true"
                            />
                            <span>{feature}</span>
                            {tooltip && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info
                                    className="text-muted-foreground h-3.5 w-3.5 shrink-0 cursor-help"
                                    aria-hidden="true"
                                  />
                                </TooltipTrigger>
                                <TooltipContent>{tooltip}</TooltipContent>
                              </Tooltip>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                    <div className="mt-6">
                      {renderButton(plan, highlighted)}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  )
}
